<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import { injectMandatory, useHttpClient, useLoadingState } from "@/vf"
import type { AxiosError } from "axios"
import { computed, ref } from "vue"
import { TaskViewMode } from "../../Issue/util"
import type {
    ChampionshipRegistrationContactPerson,
    ChampionshipRegistrationSelectTeamData,
} from "../championship-registration"
import ChampionshipRegistrationContactPersonForm from "./ChampionshipRegistrationContactPersonForm.vue"

const props = defineProps<{
    data: ChampionshipRegistrationSelectTeamData
    which: "safetyChampion" | "accuscore"
    teamId: string
}>()
const emit = defineEmits<{
    (e: "updated"): void
}>()

const http = useHttpClient()
const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")
const saving = useLoadingState()

const team = computed(() => props.data.teams.find(t => t.id === props.teamId))
const contactPerson = ref<ChampionshipRegistrationContactPerson | undefined>(
    props.which === "safetyChampion"
        ? team.value?.safetyChampionData ?? undefined
        : team.value?.accuscoreMemberData ?? undefined,
)

// Only the saved person can still be an unregistered one, the select disables all others
const selectedUnregistered = computed(() => {
    const selected = props.data.availableContactPersons.find(p => p.id === contactPerson.value?.id)

    return selected?.registered === false ? selected : null
})

const error = ref<string | null>(null)
async function submit() {
    saving.while(async () => {
        try {
            await http.put(
                `/championship/registration/${props.data.registrationId}/team/${props.teamId}/contact-person?viewMode=${viewMode}`,
                {
                    which: props.which,
                    memberId: contactPerson.value?.id,
                },
            )
        } catch (e) {
            const err = e as AxiosError<{ message: string }>
            if (err.response?.data?.message === "Championship has already started") {
                error.value = "The championship has already started. Contact persons can no longer be changed."
                return
            }
            return
        }

        emit("updated")
    })
}
</script>
<template>
    <form class="px-4" @submit.prevent="submit()">
        <div class="alert alert-danger my-3" v-if="error">
            {{ error }}
        </div>
        <div class="alert alert-info my-3">
            {{
                which === "safetyChampion"
                    ? $t("@tasks:tasks.championship_registration.safety_champion_info")
                    : $t("@tasks:tasks.championship_registration.accuscore_member_info")
            }}
        </div>
        <ChampionshipRegistrationContactPersonForm :data="data" v-model="contactPerson" require-registration />
        <div class="alert alert-warning d-flex align-items-center mt-4 mb-0" v-if="selectedUnregistered">
            <i class="fas fa-triangle-exclamation fa-lg mr-3"></i>
            <b>
                {{
                    $t("@tasks:tasks.championship_registration.contact_person_not_registered_hint", {
                        name: selectedUnregistered.name,
                    })
                }}
            </b>
        </div>

        <LoadingButton class="btn btn-primary my-4" :loading="!saving" type="submit">
            {{ $t("crud:button.save") }}
        </LoadingButton>
    </form>
</template>
